import React, { useState, useRef, useEffect } from "react";
import { API_URLS } from "../../apiUrl";
import PropertyTypeDropdown from "../customHooks/propertyTypeDropdown";
import PropertyTypeDescriptionDropdown from "../customHooks/propertyTypeDescriptionDropdown";
import UseOfPropertyDropdown from "../customHooks/useOfPropertyDropdown";
import axiosInstance from "../../components/axiosInstance";

type FormData = {
  perpose_of_use: string;
  property_type_id: any;
  property_description_id: string;
  use_of_property_id: any;
};

const AddPurposeOfProperty: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    perpose_of_use: "",
    property_type_id: 0,
    property_description_id: "",
    use_of_property_id: 0,
  });

  const [errors, setErrors] = useState<any>({});
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [dataExistMessage, setDataExistMessage] = useState<string>("");

  const formRef = useRef<HTMLFormElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const resetForm = () => {
    setFormData({
      perpose_of_use: "",
      property_type_id: 0,
      property_description_id: "",
      use_of_property_id: 0,
    });
  };

  const validateForm = (): boolean => {
    const newErrors: any = {};
    if (!formData.perpose_of_use.trim()) {
      newErrors.description_name = "Purpose Of Property is required";
    } else if (!/^[A-Za-z0-9\s]+$/.test(formData.perpose_of_use)) {
      newErrors.description_name = "Only letters, numbers, and spaces are allowed";
    }

    if (!formData.property_type_id) newErrors.property_type_id = "Property Type is required";
    if (!formData.property_description_id) newErrors.property_description_id = "Property Type Description is required";
    if (!formData.use_of_property_id) newErrors.use_of_property_id = "Use Of Property is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      try {
        const response = await axiosInstance.post(`${API_URLS.addpurposeOfProperty}`, formData);

        if (response.data.data.message) {
          resetForm();
          setDataExistMessage(`${response.data.data.message}`);
          setTimeout(() => {
            setDataExistMessage("");
          }, 5000);
        } else {
          resetForm();
          setSuccessMessage(`${response.data.message}`);
          setTimeout(() => {
            setSuccessMessage("");
          }, 5000);
        }

        setErrors({});
      } catch (error) {
        setSuccessMessage("Purpose Of Property Add failed.");
        setTimeout(() => {
          setSuccessMessage("");
        }, 5000);
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (formRef.current && !formRef.current.contains(e.target as Node)) {
        setErrors({});
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const [handleDropdownToggle, isDropdownOpen, propertyTypes, loading, setIsDropdownOpen] = PropertyTypeDropdown();
  const [handleDropdownTogglePropertyDescription, isDropdownOpenPropertyDescription, , propertyDescriptions, loadingPropertyDescription, setIsDropdownOpenPropertyDescription] = PropertyTypeDescriptionDropdown(formData.property_type_id);
  const [setIsDropdownOpenUseOfProperty, isDropdownOpenUseOfProperty, , useOfProperty, loadingUseOfProperty] = UseOfPropertyDropdown(formData.property_type_id, formData.property_description_id);

  const dropdownRefPropertyType = useRef<any>(null);
  const dropdownRefPropertyDescription = useRef<any>(null);
  const dropdownRefUseOfProperty = useRef<any>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRefPropertyType.current && !dropdownRefPropertyType.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }

      if (dropdownRefPropertyDescription.current && !dropdownRefPropertyDescription.current.contains(event.target as Node)) {
        setIsDropdownOpenPropertyDescription(false);
      }

      if (dropdownRefUseOfProperty.current && !dropdownRefUseOfProperty.current.contains(event.target as Node)) {
        setIsDropdownOpenUseOfProperty(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [setIsDropdownOpen, setIsDropdownOpenPropertyDescription, setIsDropdownOpenUseOfProperty]);

  const handlePropertyTypeDescriptionSelect = (e: any) => {
    const { value, checked } = e.target;

    setFormData((prevFormData: any) => {
      let newSelections = prevFormData.property_description_id ? prevFormData.property_description_id.split(",") : [];

      if (checked) {
        newSelections.push(value);
      } else {
        const index = newSelections.indexOf(value);
        if (index > -1) {
          newSelections.splice(index, 1);
        }
      }

      return {
        ...prevFormData,
        property_description_id: newSelections.join(","),
      };
    });
  };

  const handlePropertyTypeSelect = (selectedValue: number) => {
    setFormData((prevState) => ({
      ...prevState,
      property_type_id: selectedValue,
    }));
    setIsDropdownOpen(false);
  };

  const handlePropertyTypeSelectUseOfProperty = (selectedValue: number) => {
    setFormData((prevState) => ({
      ...prevState,
      use_of_property_id: selectedValue,
    }));
    setIsDropdownOpenUseOfProperty(false);
  };

  const selectedPropertyTypeName = propertyTypes.find((property: any) => property.type_primary_id === formData.property_type_id)?.type_name;

  const selectedPropertyDescriptions = propertyDescriptions
    .filter((property: any) => formData.property_description_id.split(",").includes(String(property.id)))
    .map((property: any) => property.type_description_name)
    .join(", ");

  const selectedUseOfProperty = useOfProperty
    .find((property: any) => property.useproperty_primary_id === formData.use_of_property_id)
    ?.use_of_property;

  const useOfPropertyDropdownToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDropdownOpenUseOfProperty((prev: any) => !prev);
  };

  return (
    <div className="max-w-full mx-auto">
      <div className="flex flex-col gap-5">
        <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Add Purpose Of Property</h1>
        <div className="p-6 bg-white rounded-lg shadow-lg dark:bg-gray-800">
          <form ref={formRef} onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2">
              <div className="relative z-[4]">
                <label htmlFor="property_type_id" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                  Property Type <span className="text-red-600">*</span>
                </label>
                <div className="relative" ref={dropdownRefPropertyType}>
                  <button
                    onClick={handleDropdownToggle}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg w-full p-2.5 pr-3 text-left flex items-center"
                    type="button"
                  >
                    {selectedPropertyTypeName || "Select Property Type"}
                    <i className="ri-arrow-down-s-line ri-lg ml-auto"></i>
                  </button>

                  {isDropdownOpen && (
                    <select
                      id="property_type_id"
                      name="property_type_id"
                      value={formData.property_type_id}
                      onChange={(e) => handlePropertyTypeSelect(Number(e.target.value))}
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-0 focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 absolute top-full"
                      size={10}
                    >
                      <option value={0} disabled>Select Property Type</option>
                      {propertyTypes.map((property: any) => (
                        <option key={property.type_primary_id} value={property.type_primary_id}>
                          {property.type_name}
                        </option>
                      ))}
                      {loading && <option>Loading...</option>}
                    </select>
                  )}
                </div>
                {errors.property_type_id && <p className="text-sm text-red-600 mt-1">{errors.property_type_id}</p>}
              </div>

              <div>
                <label htmlFor="property_description_id" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                  Property Type Description <span className="text-red-600">*</span>
                </label>
                <div className="relative" ref={dropdownRefPropertyDescription}>
                  <button
                    onClick={handleDropdownTogglePropertyDescription}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg w-full p-2.5 pr-3 text-left flex items-center"
                    type="button"
                  >
                    {selectedPropertyDescriptions || "Select Property Type Description"}
                    <i className="ri-arrow-down-s-line ri-lg ml-auto"></i>
                  </button>

                  {isDropdownOpenPropertyDescription && (
                    <div className="absolute top-full w-full bg-gray-50 border border-gray-300 rounded-lg p-2.5 dark:bg-gray-700 dark:border-gray-600 max-h-[175px] overflow-y-auto">
                      {propertyDescriptions.length === 0 ? (
                        <p className="text-sm text-red-600 dark:text-white">No Matching Property Type Description Found!</p>
                      ) : (
                        propertyDescriptions.map((property: any) => (
                          <div key={property.id} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={`property_${property.id}`}
                              value={property.id}
                              checked={formData.property_description_id.split(",").includes(String(property.id))}
                              onChange={handlePropertyTypeDescriptionSelect}
                              className="text-blue-600"
                            />
                            <label htmlFor={`property_${property.id}`} className="text-sm text-gray-900 dark:text-white">
                              {property.type_description_name}
                            </label>
                          </div>
                        ))
                      )}
                      {loadingPropertyDescription && <p>Loading...</p>}
                    </div>
                  )}
                </div>
                {errors.property_description_id && (
                  <p className="text-sm text-red-600 mt-1">{errors.property_description_id}</p>
                )}
              </div>

              <div>
                <label htmlFor="use_of_property_id" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                  Use Of The Property <span className="text-red-600">*</span>
                </label>
                <div className="relative" ref={dropdownRefUseOfProperty}>
                  <button
                    onClick={useOfPropertyDropdownToggle}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg w-full p-2.5 pr-3 text-left flex items-center"
                    type="button"
                  >
                    {selectedUseOfProperty || "Select Use Of Property"}
                    <i className="ri-arrow-down-s-line ri-lg ml-auto"></i>
                  </button>

                  {isDropdownOpenUseOfProperty && (
                    <div className="absolute top-full w-full bg-white border border-gray-300 rounded-lg mt-1 z-10">
                      {useOfProperty.length === 0 ? (
                        <p className="text-sm text-red-600 p-2">No Matching Use Of Property Found!</p>
                      ) : (
                        <select
                          id="use_of_property_id"
                          name="use_of_property_id"
                          value={formData.use_of_property_id}
                          onChange={(e) => handlePropertyTypeSelectUseOfProperty(Number(e.target.value))}
                          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-0 focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                          size={10}
                        >
                          <option value={0} disabled>Select Use Of Property</option>
                          {useOfProperty.map((property: any) => (
                            <option key={property.useproperty_primary_id} value={property.useproperty_primary_id}>
                              {property.use_of_property}
                            </option>
                          ))}
                          {loadingUseOfProperty && <option>Loading...</option>}
                        </select>
                      )}
                    </div>
                  )}
                </div>
                {errors.use_of_property_id && <p className="text-sm text-red-600 mt-1">{errors.use_of_property_id}</p>}
              </div>

              <div>
                <label htmlFor="perpose_of_use" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                  Purpose Of Property <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  id="perpose_of_use"
                  name="perpose_of_use"
                  value={formData.perpose_of_use}
                  onChange={handleChange}
                  className="block w-full p-2.5 bg-gray-50 border border-gray-300 text-sm rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                />
                {errors.description_name && <p className="text-sm text-red-600 mt-1">{errors.description_name}</p>}
              </div>
            </div>

            <div className="flex justify-between gap-5 mt-4">
              <button type="submit" className="px-5 py-2 bg-blue-600 text-white rounded-lg">Submit</button>
            </div>

            {successMessage && (
              <div className="mt-4 p-4 bg-green-600 text-white rounded-lg flex justify-between items-center">
                <span>{successMessage}</span>
                <button
                  onClick={() => setSuccessMessage('')}
                  className="ml-4 text-white font-bold text-lg focus:outline-none"
                >
                  &times;
                </button>
              </div>
            )}
            {dataExistMessage && (
              <div className="mt-4 text-red-600">{dataExistMessage}</div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddPurposeOfProperty;